import { Dialog } from "@dash/dialog";
import { FilterList, FlagFilter, SelectFilter, useQsFilters } from "@dash/filters";
import { Button, DateField } from "@dash/form";
import { Page, PageHeader } from "@dash/page";
import { SearchWithPaginationSticky } from "@dash/search";
import { QuerySkeleton } from "@dash/skeleton";
import { RowCellContent, Table, TableBody, TableHeadColumn } from "@dash/table";
import { CSSObject, useTheme } from "@emotion/react";
import React from "react";
import { MdCompareArrows, MdRefresh } from "react-icons/md";
import { useNavigate } from "react-router-dom-v5-compat";

import { ActivePrice, type PriceCurrentStatus, type PriceSortField } from "@megaron/catalog-contracts";
import { getMegaraxPath, priceListsFiltersSerializer } from "@megaron/megarax-uri";
import { useClientManager, useServiceClient } from "@megaron/react-clients";
import { Serializers, SerializerValue, type SortFilter } from "@megaron/serializers";

const filtersSerializer = priceListsFiltersSerializer;

type PriceListsFilters = SerializerValue<typeof filtersSerializer>;

export const PriceListsPage = () => {
  const client = useClientManager("catalog");
  const docs = useServiceClient("docs");
  const navigate = useNavigate();
  const defaultFilters = React.useMemo<PriceListsFilters>(
    () => ({
      priceLists: [],
      items: [],
      searchText: "",
      currentStatus: ["active", "upcoming"],
      sort: { field: "activeFrom", order: "DESC" },
    }),
    [],
  );
  const { filters: rawFilters, setFilters } = useQsFilters(filtersSerializer, defaultFilters);
  const filters = { ...defaultFilters, ...rawFilters };
  const selectedPriceLists = filters.priceLists ?? [];
  const selectedItems = filters.items ?? [];
  const selectedCurrentStatus = filters.currentStatus ?? [];

  const setFilter =
    <TKey extends keyof PriceListsFilters>(key: TKey) =>
    (value: PriceListsFilters[TKey]) =>
      setFilters({ ...filters, [key]: value });

  const priceListsQuery = client.priceListsQuery().useQuery({});
  const priceListOptions = React.useMemo(
    () => priceListsQuery.data?.priceLists.map((list) => ({ label: list.name, value: list.name })) ?? [],
    [priceListsQuery.data],
  );
  const pricesQuery = client.pricesQuery().useQuery({
    priceLists: selectedPriceLists.length > 0 ? selectedPriceLists : undefined,
    items: selectedItems.length > 0 ? selectedItems : undefined,
    searchText: filters.searchText?.trim() || undefined,
    currentStatus: selectedCurrentStatus.length > 0 ? selectedCurrentStatus : undefined,
    activeOn: filters.activeOn,
    sort: filters.sort,
  });

  return (
    <Page
      css={{
        maxWidth: "none",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <PageHeader
        actions={
          <div css={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <Button
              variant="outline"
              icon={<MdCompareArrows />}
              onClick={() =>
                navigate(getMegaraxPath.uri("megarax://catalog/diff-sync/{channel}", { channel: "sheetsPriceList" }))
              }
            >
              Sheets cenniki
            </Button>
            <Button
              variant="outline"
              icon={<MdCompareArrows />}
              onClick={() =>
                navigate(getMegaraxPath.uri("megarax://catalog/diff-sync/{channel}", { channel: "sheetsPrice" }))
              }
            >
              Sheets ceny
            </Button>
            <Button variant="outline" icon={<MdRefresh />} onClick={() => void pricesQuery.refetch()}>
              Odśwież
            </Button>
          </div>
        }
      >
        Cenniki
      </PageHeader>

      <SearchWithPaginationSticky
        onSearchChange={setFilter("searchText")}
        searchValue={filters.searchText ?? ""}
        hidePagination
      />

      <FilterList>
        <SelectFilter
          label="Cennik"
          initiallySelectedValues={selectedPriceLists}
          options={priceListOptions}
          onSelectedChange={(options) => setFilter("priceLists")(options.map((option) => option.value))}
          variant="multi-select"
          isDisabled={priceListsQuery.data === undefined}
        />
        <SelectFilter
          label="Aktualny status"
          initiallySelectedValues={selectedCurrentStatus}
          options={currentStatusOptions}
          onSelectedChange={(options) =>
            setFilter("currentStatus")(options.map((option) => option.value as PriceCurrentStatus))
          }
          variant="multi-select"
        />
        <SelectFilter
          label="Produkt"
          initiallySelectedValues={selectedItems}
          onSelectedChange={(options) => setFilter("items")(options.map((option) => option.value))}
          variant="multi-select"
          optionalNoResultsText="Wpisz id, nazwę albo EAN..."
          search={async (searchText) => {
            const results = await docs.searchItems({ searchText });

            return (
              results.value?.items.map((item) => ({
                label: [item.itemId, item.itemName, item.gtin].filter(Boolean).join(" · "),
                value: item.itemId,
              })) ?? []
            );
          }}
        />
        <ActiveOnFilter value={filters.activeOn} onChange={setFilter("activeOn")} />
      </FilterList>

      <QuerySkeleton query={pricesQuery}>
        {(data) => (
          <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <div css={{ color: "rgba(0, 0, 0, 0.6)", fontSize: "0.875rem" }}>{data.count} pozycji</div>
            <ActivePricesTable
              prices={data.prices}
              isLoading={pricesQuery.isFetching}
              activeSortValue={filters.sort}
              onSortChange={setFilter("sort")}
            />
          </div>
        )}
      </QuerySkeleton>
    </Page>
  );
};

const ActiveOnFilter = (props: { value?: Date; onChange: (date: Date | undefined) => void }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <>
      <FlagFilter
        isActive={!!props.value}
        label={props.value ? `Aktywne: ${formatDate(props.value)}` : "Aktywne dnia"}
        onClick={() => setIsOpen(true)}
        onDelete={props.value ? () => props.onChange(undefined) : undefined}
      />
      {isOpen && (
        <Dialog header="Aktywne dnia" onClose={() => setIsOpen(false)} css={{ width: "360px" }}>
          <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            <DateField label="Data" value={props.value ?? null} onChange={props.onChange} />
            <div css={{ display: "flex", justifyContent: "flex-end" }}>
              <Button onClick={() => setIsOpen(false)}>Zastosuj</Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
};

const ActivePricesTable = (props: {
  prices: ActivePrice[];
  isLoading: boolean;
  activeSortValue?: SortFilter<PriceSortField>;
  onSortChange: (sort: SortFilter<PriceSortField> | undefined) => void;
}) => {
  const theme = useTheme();

  const columns: TableHeadColumn<PriceSortField>[] = [
    sortableColumn("Cennik", "priceList"),
    sortableColumn("Id", "itemId"),
    sortableColumn("Nazwa", "itemName"),
    sortableColumn("EAN", "gtin"),
    sortableColumn("Wartość", "value"),
    { label: "Komentarz" },
    { label: "Waluta" },
    sortableColumn("Od", "activeFrom"),
    sortableColumn("Do", "activeUntilExclusive"),
    { label: "Zaakceptowane przez" },
    sortableColumn("Data akceptacji", "modifiedAt"),
  ];

  const mutedCell = { color: theme.colors.secondaryText };
  const getCells = (price: ActivePrice): RowCellContent[] => {
    const stateCellCss = getPriceStateCellCss(price, theme);
    const cell = (element: React.ReactNode, cellCss?: CSSObject): RowCellContent => ({
      element,
      cellCss: stateCellCss ? { ...cellCss, ...stateCellCss } : cellCss,
    });

    return [
      cell(price.priceList),
      cell(price.itemId, mutedCell),
      cell(price.itemName ?? "-", mutedCell),
      cell(price.gtin ?? "-", mutedCell),
      cell(price.value.toFixed(2)),
      cell(price.comment ?? "-", mutedCell),
      cell(price.currency ?? "-", mutedCell),
      cell(formatDate(price.activeFrom), mutedCell),
      cell(price.activeUntilExclusive ? formatDate(price.activeUntilExclusive) : "-", mutedCell),
      cell(price.modifiedBy, mutedCell),
      cell(formatDateTime(price.modifiedAt), mutedCell),
    ];
  };

  return (
    <div css={{ overflow: "auto", margin: "0 -2rem 1rem" }}>
      <Table<PriceSortField>
        columns={columns}
        activeSortValue={props.activeSortValue}
        onSort={props.onSortChange}
        css={{
          padding: "0 2rem",
          whiteSpace: "nowrap",
        }}
      >
        <TableBody
          isLoading={props.isLoading}
          rows={props.prices.map((price) => ({
            id: price.id,
            cellsContent: getCells(price),
          }))}
        />
      </Table>
      {props.prices.length === 0 && <div css={{ padding: "1rem 2rem", color: theme.colors.primary }}>Brak cen</div>}
    </div>
  );
};

const sortableColumn = (label: string, sortFieldName: PriceSortField): TableHeadColumn<PriceSortField> => ({
  label,
  isSortable: true,
  sortFieldName,
});

const currentStatusOptions: { label: string; value: PriceCurrentStatus }[] = [
  { label: "Aktywne", value: "active" },
  { label: "Wygasłe", value: "expired" },
  { label: "Przyszłe", value: "upcoming" },
];

const formatDate = (date: Date) => Serializers.dateOnly.serialize(date);

const formatDateTime = (date: Date) => date.toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" });

const getPriceStateCellCss = (price: ActivePrice, theme: ReturnType<typeof useTheme>): CSSObject | null => {
  const state = getPriceState(price);
  if (!state) return null;

  const stateColors = {
    expired: { background: theme.colors.dangerBackground, color: theme.colors.danger },
    upcoming: { background: theme.colors.primaryLight, color: theme.colors.primary },
    outgoing: { background: theme.colors.warningBackground, color: theme.colors.warning },
  } satisfies Record<PriceState, CSSObject>;

  return stateColors[state];
};

type PriceState = "expired" | "upcoming" | "outgoing";

const getPriceState = (price: ActivePrice): PriceState | null => {
  const activeUntilKey = price.activeUntilExclusive ? formatDate(price.activeUntilExclusive) : null;

  if (price.currentStatus === "expired") return "expired";
  if (price.currentStatus === "upcoming") return "upcoming";
  if (price.currentStatus === "active" && activeUntilKey) return "outgoing";

  return null;
};
